import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Trash2, Download, Wand2, Image as ImgIcon, Zap } from "lucide-react";

const PROMPT_PRESETS = [
  { title: "छत्तीसगढ़ ग्रामीण दृश्य", text: "Photorealistic photo of a Chhattisgarh village scene with mud houses, palm trees, and villagers in traditional clothing. Golden hour, natural lighting, high detail." },
  { title: "राजनीति प्रेस कॉन्फ्रेंस", text: "Photorealistic Indian political press conference, several journalists with mics and cameras, wooden podium, indoor conference hall, professional news wire photo style, 16:9." },
  { title: "अपराध क्राइम सीन", text: "Photorealistic Indian police at a nighttime crime scene, yellow tape, blue-red flashing lights, uniformed officers, professional journalistic photograph." },
  { title: "कृषि किसान खेत", text: "Photorealistic Indian farmer working in a green paddy field, wearing traditional white kurta and gamcha, morning golden light, tractor in background, sharp focus." },
  { title: "उत्सव त्योहार", text: "Photorealistic Indian festival celebration scene, marigold flowers, diyas, colorful crowd, rich saturated colors, festive Indian aesthetic." },
  { title: "स्कूल शिक्षा", text: "Photorealistic Indian rural school scene, children in uniform sitting on the floor, chalkboard, teacher, warm daylight, documentary photography style." },
  { title: "मंडी बाज़ार", text: "Photorealistic Indian vegetable mandi (wholesale market), colorful vegetable piles, vendors in traditional attire, buyers negotiating, bustling atmosphere, natural daylight." },
  { title: "सड़क दुर्घटना", text: "Photorealistic Indian road accident scene during daylight, damaged vehicle, police officers documenting, traffic behind, journalistic reportage style, no gore." },
  { title: "बारिश बाढ़", text: "Photorealistic Indian monsoon flood scene, submerged streets, people wading through waist-deep water, dark clouds, dramatic documentary photography." },
  { title: "सूखा जल संकट", text: "Photorealistic Indian drought-hit village, cracked dry land, empty water pots, farmer looking worried at horizon, harsh afternoon sunlight, environmental portrait." },
  { title: "अस्पताल स्वास्थ्य", text: "Photorealistic Indian government hospital ward, doctors in white coats, patients on beds, nurses attending, clean sterile environment, natural lighting through windows." },
  { title: "पुलिस बयान", text: "Photorealistic Indian police officer giving statement to media, mic bouquet, khaki uniform with badges, serious expression, indoor police station, professional press photo." },
  { title: "चुनाव रैली", text: "Photorealistic Indian political rally crowd, sea of people waving party flags, stage in distance with leader speaking, saffron/green/blue color scheme, aerial view." },
  { title: "क्रिकेट मैच", text: "Photorealistic Indian cricket stadium during a match, batsman in blue jersey playing a shot, crowd cheering with tricolor flags, floodlit evening, sports photography." },
  { title: "बॉलीवुड इवेंट", text: "Photorealistic Indian film industry red carpet event, celebrity being photographed by paparazzi, camera flashes, glamorous atmosphere, entertainment reportage." },
  { title: "बैंक ATM", text: "Photorealistic Indian bank branch or ATM scene, customers in queue, teller counter, security guard in khaki, documentary style news photo, balanced lighting." },
  { title: "मंदिर धार्मिक", text: "Photorealistic Indian Hindu temple scene, devotees offering prayers, diyas lit, flower garlands, marble flooring, warm devotional atmosphere, respectful photography." },
  { title: "रेलवे स्टेशन", text: "Photorealistic Indian railway station platform, passengers with luggage waiting, train arriving, railway announcements board, mix of colors and cultures, travel photography." },
  { title: "किसान आंदोलन", text: "Photorealistic Indian farmer protest, tractors on highway, banners in Hindi, farmers wearing turbans, determined faces, wide-angle documentary style." },
  { title: "स्टार्टअप ऑफिस", text: "Photorealistic Indian tech startup modern office, young diverse team working on laptops, whiteboards with plans, bright airy space, corporate reportage style." },
  { title: "छत्तीसगढ़ आदिवासी", text: "Photorealistic Chhattisgarh tribal community scene, traditional Baiga/Gond attire, mud dwellings, elders and children, warm earthy tones, ethnographic documentary photography." },
  { title: "पत्रकार रिपोर्टिंग", text: "Photorealistic Indian journalist in the field reporting, holding a mic with news channel logo, camera crew behind, on-location outdoor lighting, breaking-news feel." },
  { title: "युवा साइबर कैफे", text: "Photorealistic Indian youth using computers/mobile phones, cyber cafe or coaching center, modern casual clothes, focused expressions, diagonal composition." },
  { title: "बजट लोक सभा", text: "Photorealistic Indian Parliament Lok Sabha chamber, MPs seated on green benches, Speaker's chair visible, official-look photograph, wide-angle interior." },
  { title: "मुख्यमंत्री बैठक", text: "Photorealistic Indian Chief Minister meeting officials, formal setting with Indian flag behind, roundtable discussion, professional government press photograph." },
  { title: "पहलवान अखाड़ा", text: "Photorealistic Indian wrestling akhara scene, wrestlers practicing on mud, coach observing, traditional attire, warm dusty atmosphere, sports documentary style." },
];

export default function AIImageGenPage() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("gemini-3.1-flash-image-preview");
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [filter, setFilter] = useState("");
  const [refFile, setRefFile] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = () => api.get("/admin/image-gen/gallery", { params: filter ? { source: filter } : {} }).then((r) => setGallery(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Prompt daalen");
    setBusy(true);
    try {
      let data;
      if (refFile) {
        // Reference image edit — multipart
        const fd = new FormData();
        fd.append("prompt", prompt.trim());
        fd.append("file", refFile);
        fd.append("model", model);
        const r = await api.post("/admin/image-gen/upload-reference", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
        data = r.data;
      } else {
        const r = await api.post("/admin/image-gen/generate", { prompt: prompt.trim(), model }, { timeout: 120000 });
        data = r.data;
      }
      toast.success(`✨ Image ready — ${data.size_kb}KB`);
      setPrompt(""); setRefFile(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const genLogoPack = async () => {
    if (!window.confirm("Publisher Center ke liye 4 logo variants generate karein? Ismein 40-60 sec lag sakte hain.")) return;
    setLogoBusy(true);
    try {
      const { data } = await api.post("/admin/image-gen/logo-pack", {}, { timeout: 300000 });
      const okCount = (data.variants || []).filter(v => !v.error).length;
      toast.success(`✅ ${okCount}/4 logo variants generated · gallery mein dekh lo`);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLogoBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Image delete karein?")) return;
    try { await api.delete(`/admin/image-gen/${id}`); toast.success("Deleted"); load(); }
    catch(e) { toast.error(formatError(e)); }
  };

  const copyUrl = (url) => {
    const full = `${backend}${url}`;
    navigator.clipboard?.writeText(full);
    toast.success("URL copy hua");
  };

  const sourceLabel = { manual: "Manual", article_auto: "Article Auto", pdf_ad: "PDF Ad", reference: "Reference" };
  const sourceColor = { manual: "bg-fuchsia-100 text-fuchsia-700", article_auto: "bg-emerald-100 text-emerald-700", pdf_ad: "bg-amber-100 text-amber-700", reference: "bg-sky-100 text-sky-700" };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="ai-image-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">AI Image Generation · Nano Banana</div>
        <h1 className="font-devnagri-heading text-2xl md:text-3xl font-black text-[#0F172A] flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-fuchsia-500"/> AI Photo Studio
        </h1>
        <p className="text-sm text-slate-500 mt-1">Prompt daalo → Gemini Nano Banana 5-10 sec me photo bana dega · Article/Ad/Short bg sab jagah lagao</p>
      </div>

      {/* Prompt panel */}
      <div className="rounded-xl bg-white border-2 border-slate-100 overflow-hidden">
        <div className="px-4 py-3 border-b-2 border-slate-100 bg-gradient-to-r from-fuchsia-50 to-purple-50 flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-fuchsia-600"/>
          <div className="font-devnagri-heading text-lg font-black text-[#0F172A]">नई image generate karo</div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-black text-slate-500">Prompt (Hindi ya English)</Label>
            <Textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder={refFile ? "Reference image me kya change karna hai batao..." : "e.g. Photorealistic photo of Indian farmers protesting in Delhi with tractors and flags..."}
              className="mt-1" data-testid="prompt-input"/>
          </div>
          {/* Reference image upload (optional) */}
          <div className="flex items-center gap-3">
            <Label className="text-[11px] uppercase tracking-widest font-black text-slate-500 shrink-0">Reference Image (optional):</Label>
            <input type="file" accept="image/*" onChange={(e) => setRefFile(e.target.files?.[0] || null)}
              className="text-xs flex-1" data-testid="ref-file-input"/>
            {refFile && (
              <button onClick={() => setRefFile(null)} className="text-xs text-red-600 hover:underline" data-testid="ref-clear">
                ✕ Clear
              </button>
            )}
          </div>
          {refFile && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
              📎 <b>{refFile.name}</b> uploaded — ab prompt mein batao ki isme kya edit karna hai (e.g. "isko rainy day mein badlo")
            </div>
          )}
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-black text-slate-500 mb-1.5 block">तुरंत Presets — click karo</Label>
            <div className="flex flex-wrap gap-2">
              {PROMPT_PRESETS.map((p) => (
                <button key={p.title} onClick={() => setPrompt(p.text)}
                  className="text-xs bg-slate-100 hover:bg-fuchsia-100 text-slate-700 hover:text-fuchsia-700 px-2.5 py-1 rounded-full font-bold transition"
                  data-testid={`preset-${p.title}`}>
                  {p.title}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select value={model} onChange={(e) => setModel(e.target.value)}
              className="text-xs border-2 border-slate-200 rounded-lg px-2 py-2 font-bold focus:border-fuchsia-500"
              data-testid="model-select">
              <optgroup label="Gemini Nano Banana (Free · Photos)">
                <option value="gemini-3.1-flash-image-preview">Nano Banana Flash · Fast (10s)</option>
                <option value="gemini-3-pro-image-preview">Nano Banana Pro · HD (20s)</option>
              </optgroup>
              <optgroup label="OpenAI GPT Image (Free · Text-in-image)">
                <option value="gpt-image-1">GPT Image 1 · Best for banners/posters (30s)</option>
                <option value="dall-e-3">DALL-E 3 · Artistic (25s)</option>
              </optgroup>
            </select>
            <Button onClick={generate} disabled={busy}
              className="flex-1 bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:brightness-110 text-white font-black gap-2"
              data-testid="generate-btn">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating (5-10s)…</> : <><Zap className="w-4 h-4"/> Generate</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Publisher Center Logo Pack CTA */}
      <div className="rounded-xl bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 border-2 border-amber-200 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest font-black text-amber-700">📰 Google News Publisher Center</div>
          <div className="font-devnagri-heading text-lg font-black text-[#0F172A]">Logo Pack — 4 variants ek click me</div>
          <div className="text-xs text-slate-600 mt-1">Square 1000×1000 · Wordmark 200×200 · Horizontal 1000×200 · Cover 1920×1080 — sab Publisher Center approval ke liye ready.</div>
        </div>
        <Button onClick={genLogoPack} disabled={logoBusy}
          className="bg-amber-600 hover:bg-amber-700 text-white font-black gap-2"
          data-testid="logo-pack-btn">
          {logoBusy ? <><Loader2 className="w-4 h-4 animate-spin"/> 4 logos ban rahe (40-60s)…</> : <><Sparkles className="w-4 h-4"/> Generate Logo Pack</>}
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-widest font-black text-slate-500 mr-2">Filter:</span>
        {[
          { k: "", label: "All", count: gallery.length },
          { k: "manual", label: "Manual" },
          { k: "article_auto", label: "Article Auto" },
          { k: "pdf_ad", label: "PDF Ad" },
          { k: "reference", label: "Reference" },
          { k: "logo_pack", label: "Logo Pack" },
        ].map((t) => (
          <button key={t.k} onClick={() => setFilter(t.k)}
            className={`text-xs font-black px-3 py-1.5 rounded-full transition ${filter === t.k ? "bg-fuchsia-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`filter-${t.k || 'all'}`}>
            {t.label}{t.count !== undefined ? ` (${t.count})` : ""}
          </button>
        ))}
      </div>

      {/* Gallery */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {gallery.map((g) => (
          <div key={g.id || g.file_id} className="group relative rounded-xl overflow-hidden border-2 border-slate-100 bg-white hover:border-fuchsia-300 transition" data-testid={`gallery-${g.file_id}`}>
            <img src={`${backend}${g.url}`} alt={g.prompt} loading="lazy"
              className="w-full aspect-square object-cover bg-slate-50"/>
            <div className="p-2 space-y-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${sourceColor[g.source] || 'bg-slate-100 text-slate-600'}`}>{sourceLabel[g.source] || g.source}</span>
                <span className="text-[9px] text-slate-400">{g.size_kb}KB</span>
              </div>
              <div className="text-[10px] text-slate-600 line-clamp-2" title={g.prompt}>{g.prompt}</div>
            </div>
            <div className="absolute inset-x-0 top-0 flex justify-end gap-1 p-1.5 opacity-0 group-hover:opacity-100 transition bg-gradient-to-b from-black/40 to-transparent">
              <button onClick={() => copyUrl(g.url)} className="p-1.5 bg-white/90 rounded hover:bg-white text-slate-700" title="Copy URL" data-testid={`copy-${g.file_id}`}>
                <Copy className="w-3.5 h-3.5"/>
              </button>
              <a href={`${backend}${g.url}`} download={`ai-image-${g.file_id}.png`} className="p-1.5 bg-white/90 rounded hover:bg-white text-slate-700" title="Download" data-testid={`download-${g.file_id}`}>
                <Download className="w-3.5 h-3.5"/>
              </a>
              <button onClick={() => del(g.file_id)} className="p-1.5 bg-white/90 rounded hover:bg-red-100 text-red-600" title="Delete" data-testid={`del-${g.file_id}`}>
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          </div>
        ))}
        {gallery.length === 0 && (
          <div className="col-span-2 md:col-span-3 lg:col-span-4 p-12 text-center text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl">
            <ImgIcon className="w-12 h-12 mx-auto mb-2 text-slate-300"/>
            Abhi tak koi image nahi banai. Upar prompt daalo aur Generate dabao!
          </div>
        )}
      </div>
    </div>
  );
}
